import type {
  Point,
  TransformState,
} from "../../../../../types/stage-types.js";
import type { GridConfig } from "../GridManager/GridManager.js";

/**
 * HUD display configuration options
 */
export interface HUDConfig {
  readonly visible?: boolean;
  readonly container?: HTMLElement;
  readonly className?: string;
}

/**
 * HUD section data for rendering
 */
export interface HUDSectionData {
  readonly title: string;
  readonly content: readonly string[];
}

/**
 * Complete HUD data for display
 */
export interface HUDData {
  readonly transform: TransformState;
  readonly stageBounds: DOMRect | null;
  readonly origin: Point;
  readonly originInStage: Point;
  readonly cursor: {
    readonly stagePosition: Point | null;
    readonly productPosition: Point | null;
    readonly tracking: boolean;
  };
  readonly grid: {
    readonly config: GridConfig;
    readonly dotGridVisible: boolean;
    readonly linearGridVisible: boolean;
  };
  readonly originTick: {
    readonly visible: boolean;
  };
  readonly boundary: {
    readonly visible: boolean;
    readonly label: string;
  };
}

/**
 * HUD state interface
 */
interface HUDState {
  readonly visible: boolean;
  readonly lastData: HUDData | null;
}

/**
 * HUDDisplay API for managing debug information display
 */
export interface HUDDisplayAPI {
  readonly update: (data: HUDData) => void;
  readonly setVisible: (visible: boolean) => void;
  readonly clear: () => void;
  readonly getState: () => Readonly<HUDState>;
  readonly destroy: () => void;
}

/**
 * Creates a HUDDisplay for handling debug information display
 * @param container - Container element for the HUD
 * @param config - HUD display configuration options
 * @returns HUDDisplay API
 */
export const createHUDDisplay = (
  container: HTMLElement,
  config: HUDConfig = {}
): HUDDisplayAPI => {
  console.log("📊 Creating HUDDisplay");

  let isDestroyed = false;

  // Configuration with defaults
  const hudConfig = {
    visible: config.visible !== false, // Default to true
    container: config.container || container,
    className: config.className || "ui-helpers__info",
  };

  // HUD state
  let state: HUDState = {
    visible: hudConfig.visible,
    lastData: null,
  };

  // DOM elements for HUD
  let hudElement: HTMLElement | null = null;

  /**
   * Create HUD container element
   */
  const createHUDElement = (): void => {
    if (hudElement) return; // Already created

    hudElement = document.createElement("div");
    hudElement.className = hudConfig.className;
    container.appendChild(hudElement);
  };

  /**
   * Format transform section
   */
  const formatTransformSection = (
    transform: TransformState
  ): HUDSectionData => ({
    title: "Transform",
    content: [
      `Position: (${transform.translateX.toFixed(
        1
      )}, ${transform.translateY.toFixed(1)})`,
      `Scale: ${transform.scale.toFixed(3)}x (${(transform.scale * 100).toFixed(
        1
      )}%)`,
      `Rotation: ${transform.rotation.toFixed(1)}°`,
    ],
  });

  /**
   * Format stage section
   */
  const formatStageSection = (stageBounds: DOMRect | null): HUDSectionData => {
    const stageWidth = stageBounds?.width || 0;
    const stageHeight = stageBounds?.height || 0;

    return {
      title: "Stage",
      content: [
        `Size: ${stageWidth.toFixed(0)} × ${stageHeight.toFixed(0)} px`,
        `Center: (${(stageWidth / 2).toFixed(1)}, ${(stageHeight / 2).toFixed(
          1
        )})`,
        `Pixel Ratio: ${(window.devicePixelRatio || 1).toFixed(1)}x`,
      ],
    };
  };

  /**
   * Format coordinate origin section
   */
  const formatOriginSection = (
    origin: Point,
    originInStage: Point
  ): HUDSectionData => ({
    title: "Coordinate Origin",
    content: [
      `Product: (${origin.x.toFixed(1)}, ${origin.y.toFixed(1)})`,
      `Stage: (${originInStage.x.toFixed(1)}, ${originInStage.y.toFixed(1)})`,
    ],
  });

  /**
   * Format cursor section
   */
  const formatCursorSection = (cursor: HUDData["cursor"]): HUDSectionData => {
    if (cursor.stagePosition && cursor.productPosition) {
      return {
        title: "Cursor",
        content: [
          `Stage: (${cursor.stagePosition.x.toFixed(
            1
          )}, ${cursor.stagePosition.y.toFixed(1)})`,
          `Product: (${cursor.productPosition.x.toFixed(
            1
          )}, ${cursor.productPosition.y.toFixed(1)})`,
          `Tracking: ${cursor.tracking ? "✅ Active" : "❌ Disabled"}`,
        ],
      };
    }

    return {
      title: "Cursor",
      content: ["Position: Not tracking"],
    };
  };

  /**
   * Format grid config section
   */
  const formatGridConfigSection = (gridConfig: GridConfig): HUDSectionData => ({
    title: "Grid Config",
    content: [
      `Dot: ${gridConfig.dotSpacing}px, ${gridConfig.dotSize}px size`,
      `Line: ${gridConfig.lineSpacing}px spacing`,
      `Major: every ${gridConfig.majorGridInterval}px`,
      `Area: ±${gridConfig.widthOffset}% of product width`,
      `Opacity: ${(gridConfig.dotOpacity! * 100).toFixed(0)}%/${(
        gridConfig.majorDotOpacity! * 100
      ).toFixed(0)}%`,
    ],
  });

  /**
   * Format dot grid section
   */
  const formatDotGridSection = (
    grid: HUDData["grid"],
    transform: TransformState
  ): HUDSectionData => ({
    title: "Dot Grid",
    content: [
      `Spacing: ${grid.config.dotSpacing}px → ${(
        grid.config.dotSpacing! * transform.scale
      ).toFixed(1)}px`,
      `Status: ${grid.dotGridVisible ? "✅ Visible" : "❌ Hidden"}`,
      `Style: ${grid.config.dotSize}×${grid.config.dotSize}px circles`,
      "Press 'G' to toggle",
    ],
  });

  /**
   * Format linear grid section
   */
  const formatLinearGridSection = (
    grid: HUDData["grid"],
    transform: TransformState
  ): HUDSectionData => ({
    title: "Linear Grid",
    content: [
      `Spacing: ${grid.config.lineSpacing}px → ${(
        grid.config.lineSpacing! * transform.scale
      ).toFixed(1)}px`,
      `Status: ${grid.linearGridVisible ? "✅ Visible" : "❌ Hidden"}`,
      `Style: Lines, ${(grid.config.lineOpacity! * 100).toFixed(0)}% opacity`,
      "Press 'L' to toggle",
    ],
  });

  /**
   * Format origin tick section
   */
  const formatOriginTickSection = (
    originTick: HUDData["originTick"],
    originInStage: Point
  ): HUDSectionData => ({
    title: "Origin Tick",
    content: [
      `Status: ${originTick.visible ? "✅ Visible" : "❌ Hidden"}`,
      `Position: (${originInStage.x.toFixed(1)}, ${originInStage.y.toFixed(
        1
      )})`,
    ],
  });

  /**
   * Format boundary section
   */
  const formatBoundarySection = (
    boundary: HUDData["boundary"]
  ): HUDSectionData => ({
    title: "Product Boundary",
    content: [
      `Status: ${boundary.visible ? "✅ Visible" : "❌ Hidden"}`,
      `Label: "${boundary.label}"`,
      "Type: DIV with dynamic positioning",
    ],
  });

  /**
   * Render HUD section as HTML
   */
  const renderSection = (section: HUDSectionData): string => {
    const contentHTML = section.content
      .map((line) => {
        // Check if line should be bold (contains "Press" instructions)
        if (line.includes("Press")) {
          return `<p><strong>${line}</strong></p>`;
        }
        return `<p>${line}</p>`;
      })
      .join("");

    return `
      <div class="hud-section">
        <h4>${section.title}</h4>
        ${contentHTML}
      </div>
    `;
  };

  /**
   * Update HUD display with new data
   */
  const update = (data: HUDData): void => {
    if (isDestroyed || !state.visible) return;

    // Create HUD element if it doesn't exist
    if (!hudElement) {
      createHUDElement();
    }

    if (!hudElement) return;

    // Format all sections
    const sections: HUDSectionData[] = [
      formatTransformSection(data.transform),
      formatStageSection(data.stageBounds),
      formatOriginSection(data.origin, data.originInStage),
      formatCursorSection(data.cursor),
      formatGridConfigSection(data.grid.config),
      formatDotGridSection(data.grid, data.transform),
      formatLinearGridSection(data.grid, data.transform),
      formatOriginTickSection(data.originTick, data.originInStage),
      formatBoundarySection(data.boundary),
    ];

    // Render all sections
    const html = sections.map(renderSection).join("");
    hudElement.innerHTML = html;

    // Update state
    state = { ...state, lastData: data };
  };

  /**
   * Set HUD visibility
   */
  const setVisible = (visible: boolean): void => {
    if (isDestroyed) return;

    state = { ...state, visible };

    if (hudElement) {
      hudElement.style.display = visible ? "block" : "none";
    }

    // Re-render with last data if becoming visible
    if (visible && state.lastData) {
      update(state.lastData);
    }
  };

  /**
   * Clear HUD display
   */
  const clear = (): void => {
    if (isDestroyed) return;

    if (hudElement) {
      hudElement.innerHTML = "";
    }

    state = { ...state, lastData: null };
  };

  /**
   * Get current HUD state
   */
  const getState = (): Readonly<HUDState> => ({ ...state });

  /**
   * Cleanup resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    if (hudElement && hudElement.parentNode) {
      hudElement.parentNode.removeChild(hudElement);
    }

    hudElement = null;
    state = { visible: false, lastData: null };
  };

  // Initialize - create HUD if visible
  if (state.visible) {
    createHUDElement();
  }

  console.log("HUDDisplay initialized");

  return {
    update,
    setVisible,
    clear,
    getState,
    destroy,
  };
};
